// AdminBulkPayouts — admin-only page for launching mass-credit
// campaigns. Lists past + running jobs at the top with progress bars,
// and a "+ New campaign" CTA that opens a modal with the audience
// + amount form.
//
// Rebuilt in light + dark new aesthetic.
//
// Phase 3 (2026-05-15): mounted inside PlayersHub as the "Bulk" sub-tab
// at /people/players/bulk. The hub already provides the page-level
// max-w wrapper + the "Players" header above the sub-tab strip — so
// this panel drops its own outer wrapper, its stale "Back to More"
// link, and its own page-level "Bulk payouts" title. The action
// surface ("+ New campaign" + job cards) sits directly under the
// hub's sub-tab strip.

import { useEffect, useRef, useState } from 'react'; /* useEffect used by both the panel + the modal's geo cascade */
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { INPUT_CLS } from '../../components/fieldStyles';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';

type JobRow = {
  id: number;
  label: string;
  amount_per_user: number;
  status: 'queued' | 'resolving' | 'running' | 'done' | 'partial' | 'error';
  total_targets: number;
  done_targets: number;
  failed_targets: number;
  credited_total: number;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
};

const STATUS_PILL: Record<JobRow['status'], string> = {
  queued:    'bg-arena-100/60 text-arena-700 ring-1 ring-inset ring-arena-200/60 dark:bg-white/5 dark:text-white/60 dark:ring-white/10',
  resolving: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200/60 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/30',
  running:   'bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-200/60 dark:bg-cyan-400/10 dark:text-cyan-300 dark:border-cyan-400/30',
  done:      'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200/60 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/30',
  partial:   'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200/60 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/30',
  error:     'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200/60 dark:bg-rose-400/10 dark:text-rose-300 dark:ring-rose-400/30',
};

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

export default function BulkPayoutsPanel() {
  const { admin } = useAdminAuth();
  const [rows, setRows] = useState<JobRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // 2026-05-20 — 429-remediation.
  //   • Interval bumped from 5 s → 15 s. The previous cadence was
  //     overkill for backend bulk-payout job state, which doesn't
  //     change anywhere near that fast.
  //   • Removed `rows` from the polling effect's dep array. The old
  //     pattern tore down + recreated the setInterval on EVERY
  //     successful poll because setRows() produces a new array
  //     reference. Now we keep one stable interval and check the
  //     inflight condition via a ref inside the tick.
  //   • `load` uses a functional setRows so deps don't include rows.
  //   • One mount-time effect schedules the timer; cleanup runs on
  //     unmount only.
  const inflightRef = useRef(false);
  async function load() {
    try {
      const r = await adminApi.get<{ rows: JobRow[] }>('/admin/bulk-payouts?limit=30');
      setRows(r.rows);
      inflightRef.current = r.rows.some((j) =>
        ['queued', 'resolving', 'running'].includes(j.status)
      );
    } catch (e) { setErr((e as ApiError).message); }
  }
  useEffect(() => {
    void load();                                   // initial fetch on mount
    const t = setInterval(() => {
      if (!inflightRef.current) return;             // idle when nothing running
      if (typeof document !== 'undefined' && document.hidden) return;  // skip background tab
      void load();
    }, 15_000);
    return () => clearInterval(t);
  }, []);

  if (admin?.role !== 'admin') {
    return (
      <div className="max-w-screen-2xl mx-auto px-2 sm:px-3 lg:px-6 py-6">
        <div className="rounded-2xl bg-rose-50 dark:bg-rose-500/10 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30 text-rose-800 dark:text-rose-200 p-8 text-center">
          <Icon name="lock" size={28} className="mx-auto mb-2" />
          <div className="font-bold">Admin only</div>
        </div>
      </div>
    );
  }

  return (
    // Phase 3 (2026-05-15): outer max-w wrapper, Back-to-More link, and
    // page-level "Bulk payouts" header all removed. The PlayersHub
    // owns the gutter + the page title; this panel only renders the
    // sub-tab content: an action row + job cards.
    <div className="space-y-4">
      <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <span className="grid place-items-center w-10 h-10 rounded-xl
                           bg-sky-50 text-sky-700 ring-1 ring-sky-100
                           dark:bg-sky-400/15 dark:text-sky-300 dark:ring-sky-400/30">
            <Icon name="forklift" size={22} filled />
          </span>
          <div className="min-w-0">
            <h2 className="text-[15px] font-display font-semibold text-arena-900 dark:text-white tracking-tight">
              Bulk payouts
            </h2>
            <p className="text-[12px] text-arena-600 dark:text-neon-200/65 truncate">
              Mass credit campaigns
            </p>
          </div>
        </div>
        <button onClick={() => setCreating(true)}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full
                           bg-sky-600 hover:bg-sky-500 active:bg-sky-700
                           text-white text-[13px] font-bold shadow-[0_2px_10px_rgba(14,165,233,0.35)]
                           transition active:scale-95">
          <Icon name="add" size={16} /> New campaign
        </button>
      </motion.div>

      {err && (
        <div className="rounded-2xl bg-rose-50 dark:bg-rose-500/10
                        ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30
                        text-rose-800 dark:text-rose-200 px-4 py-3 text-sm">
          {err}
        </div>
      )}

      <div className="space-y-2.5">
        {rows.length === 0 && (
          <div className="rounded-2xl bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-10 text-center">
            <Icon name="forklift" size={32} className="text-arena-400 dark:text-neon-200/35 mx-auto mb-2" />
            <p className="text-[13px] text-arena-700 dark:text-neon-200/75 font-semibold">No campaigns yet</p>
            <p className="text-[11.5px] text-arena-600 dark:text-neon-200/55 mt-0.5">
              Tap "+ New campaign" to credit a group of players in one go.
            </p>
          </div>
        )}
        {rows.map((j, i) => {
          const pct = j.total_targets > 0
            ? Math.round((j.done_targets + j.failed_targets) / j.total_targets * 100)
            : 0;
          return (
            <motion.div
              key={j.id}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: Math.min(i * 0.02, 0.15) }}
              className="rounded-2xl p-4
                         bg-white dark:bg-arena-700/40
                         ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                         shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none"
            >
              <div className="flex items-start gap-3">
                <span className="grid place-items-center w-10 h-10 rounded-xl
                                 bg-sky-50 text-sky-700
                                 dark:bg-sky-400/15 dark:text-sky-300 shrink-0">
                  <Icon name="forklift" size={20} filled />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-[14px] text-neutral-900 dark:text-white truncate">{j.label}</h3>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${STATUS_PILL[j.status]}`}>
                      {j.status}
                    </span>
                  </div>
                  <p className="text-[11px] text-arena-600 dark:text-neon-200/70 truncate">
                    {inr(j.amount_per_user)}/user · {new Date(j.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  </p>

                  {j.total_targets > 0 && (
                    <div className="mt-3">
                      <div className="h-1.5 bg-arena-100/60 dark:bg-arena-700/60 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${
                            j.status === 'error' ? 'bg-rose-500' :
                            j.status === 'partial' ? 'bg-amber-500' :
                            'bg-emerald-500'
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="mt-1.5 flex justify-between text-[10.5px] text-arena-600 dark:text-neon-200/70 tabular-nums">
                        <span>{j.done_targets}/{j.total_targets} done · {j.failed_targets} failed</span>
                        <span className="text-emerald-700 dark:text-emerald-300 font-semibold">
                          {inr(j.credited_total)} paid
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {creating && (
        <CreateCampaignModal
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

// ─── Modal ──────────────────────────────────────────────────────────

// Minimal row shapes returned by the public /api/geo/* endpoints.
// We mirror them locally instead of importing because BulkPayoutsPanel
// is the only consumer here and the shapes are tiny.
type GeoState    = { id: number; name: string };
type GeoDistrict = { id: number; state_id: number; name: string };
type GeoArea     = { id: number; district_id: number; name: string };

function CreateCampaignModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [label,    setLabel]    = useState('');
  const [amount,   setAmount]   = useState(50);
  // Phase R4 cleanup — geo filter now uses structured FK ids fetched
  // from /api/geo/states + /api/geo/districts + /api/geo/areas
  // (the same endpoints the LocationCapture component uses). This
  // replaces the old free-text inputs that wrote into the now-
  // deprecated `users.legacy_state` / `users.legacy_district` columns.
  const [stateId,    setStateId]    = useState<number | ''>('');
  const [districtId, setDistrictId] = useState<number | ''>('');
  const [areaId,     setAreaId]     = useState<number | ''>('');
  const [segment,  setSegment]  = useState('');
  const [maxTargets, setMax]    = useState<number | ''>('');
  const [busy,     setBusy]     = useState(false);
  const [err,      setErr]      = useState<string | null>(null);

  // Lazy-loaded geo cascade. States load on mount (cheap, ~37 rows);
  // districts load when a state is picked; areas load when a district
  // is picked. This mirrors LocationCapture's pattern so the user
  // experience is consistent across the platform.
  const [states,    setStates]    = useState<GeoState[]>([]);
  const [districts, setDistricts] = useState<GeoDistrict[]>([]);
  const [areas,     setAreas]     = useState<GeoArea[]>([]);

  // Load states once on mount.
  useEffect(() => {
    fetch('/api/geo/states')
      .then((r) => r.json())
      .then((j) => { if (j.ok) setStates(j.data); })
      .catch(() => { /* silent — operator can still target by segment / agent_id */ });
  }, []);

  // Load districts whenever the state changes; clear downstream picks.
  useEffect(() => {
    setDistricts([]); setAreas([]);
    setDistrictId(''); setAreaId('');
    if (!stateId) return;
    fetch(`/api/geo/districts?state_id=${stateId}`)
      .then((r) => r.json())
      .then((j) => { if (j.ok) setDistricts(j.data); })
      .catch(() => {});
  }, [stateId]);

  // Load areas whenever the district changes; clear downstream pick.
  useEffect(() => {
    setAreas([]); setAreaId('');
    if (!districtId) return;
    fetch(`/api/geo/areas?district_id=${districtId}`)
      .then((r) => r.json())
      .then((j) => { if (j.ok) setAreas(j.data); })
      .catch(() => {});
  }, [districtId]);

  async function submit() {
    setErr(null);
    if (!label.trim()) return setErr('Label required');
    if (!amount || amount < 1) return setErr('Amount must be ≥ 1');
    const audience: Record<string, unknown> = {};
    // Forward structured IDs (Phase R4 cleanup). Backend audienceSchema
    // is .strict() and rejects the old text keys, so the keys we emit
    // here MUST match the backend whitelist exactly.
    if (stateId    !== '') audience.state_id    = Number(stateId);
    if (districtId !== '') audience.district_id = Number(districtId);
    if (areaId     !== '') audience.area_id     = Number(areaId);
    if (segment)           audience.segment     = segment;
    if (Object.keys(audience).length === 0) {
      return setErr('Pick at least one audience filter (state / district / area / segment)');
    }
    // Irreversible mass real-money credit — confirm scope + amount first
    // (audit 2026-08-05: fired with no recipient/₹ preview and no confirmation).
    const filterDesc = [
      stateId    !== '' ? `state #${stateId}`       : null,
      districtId !== '' ? `district #${districtId}`  : null,
      areaId     !== '' ? `area #${areaId}`          : null,
      segment            ? `segment "${segment}"`    : null,
    ].filter(Boolean).join(' · ');
    const cap = (maxTargets && Number(maxTargets) > 0) ? `capped at ${maxTargets} users` : 'NO cap — EVERY matching user';
    if (!confirm(`Credit ₹${amount} to every matching user\n\nAudience: ${filterDesc}\nLimit: ${cap}\n\nThis pays REAL money and CANNOT be undone. Continue?`)) return;
    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        label: label.trim(),
        audience,
        amount_per_user: amount,
      };
      if (maxTargets && Number(maxTargets) > 0) body.max_targets = Number(maxTargets);
      await adminApi.post('/admin/bulk-payouts', body);
      onCreated();
    } catch (e) {
      setErr((e as ApiError).message);
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] grid place-items-start sm:place-items-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 360, damping: 28 }}
        className="w-full max-w-md rounded-2xl
                   bg-white dark:bg-arena-800
                   ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                   p-5 space-y-3 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className="grid place-items-center w-9 h-9 rounded-xl
                           bg-sky-50 text-sky-700
                           dark:bg-sky-400/15 dark:text-sky-300">
            <Icon name="forklift" size={18} filled />
          </span>
          <h2 className="text-lg font-bold text-neutral-900 dark:text-white">New campaign</h2>
        </div>

        <Field label="Label">
          <input value={label} onChange={(e) => setLabel(e.target.value)}
                 placeholder="Diwali bonus ₹50 GJ" className={inputCls} />
        </Field>

        <Field label="Amount per user (₹)">
          <input type="number" min={1} value={amount}
                 onChange={(e) => setAmount(Number(e.target.value) || 0)} className={inputCls} />
        </Field>

        <div className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mt-2 font-semibold">
          Audience filter · ≥ 1 required
        </div>

        {/* Phase R4 cleanup — structured geo cascade.
            State → District → Area, each dependent dropdown loads
            lazily as the previous one is picked. The same /api/geo/*
            endpoints power LocationCapture (signup + profile + admin
            edit), so the option universe is identical everywhere on
            the platform. Empty selection = no geo filter on that
            level (e.g. just State picked = state-wide audience). */}
        <Field label="State">
          <select value={stateId === '' ? '' : String(stateId)}
                  onChange={(e) => setStateId(e.target.value ? Number(e.target.value) : '')}
                  className={inputCls}>
            <option value="">— any state —</option>
            {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <Field label="District">
          <select value={districtId === '' ? '' : String(districtId)}
                  onChange={(e) => setDistrictId(e.target.value ? Number(e.target.value) : '')}
                  disabled={!stateId}
                  className={inputCls}>
            <option value="">{stateId ? '— any district —' : '— pick a state first —'}</option>
            {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Area (optional, narrowest)">
          <select value={areaId === '' ? '' : String(areaId)}
                  onChange={(e) => setAreaId(e.target.value ? Number(e.target.value) : '')}
                  disabled={!districtId}
                  className={inputCls}>
            <option value="">{districtId ? '— any area —' : '— pick a district first —'}</option>
            {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </Field>
        <Field label="Segment">
          <select value={segment} onChange={(e) => setSegment(e.target.value)} className={inputCls}>
            <option value="">— none —</option>
            <option value="new_users">new_users (joined &lt; 7 days)</option>
            <option value="big_spenders">big_spenders (&gt; ₹5000 lifetime)</option>
            <option value="inactive_30d">inactive_30d (no match in 30d)</option>
          </select>
        </Field>

        <Field label="Max targets (safety cap, optional)">
          <input type="number" min={1} value={maxTargets}
                 onChange={(e) => setMax(e.target.value ? Number(e.target.value) : '')}
                 placeholder="leave blank = no cap" className={inputCls} />
        </Field>

        {err && (
          <div className="rounded-lg bg-rose-50 dark:bg-rose-500/10 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30 text-rose-700 dark:text-rose-200 text-sm px-3 py-2">{err}</div>
        )}

        <div className="flex gap-2 justify-end pt-1">
          <button onClick={onClose} disabled={busy}
                  className="px-4 py-2 rounded-full text-[13px] font-semibold
                             bg-white dark:bg-arena-700/40
                             ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                             text-arena-700 dark:text-neon-200/75
                             hover:bg-arena-50 dark:hover:bg-arena-700/60">Cancel</button>
          <button onClick={submit} disabled={busy}
                  className="px-4 py-2 rounded-full text-[13px] font-bold
                             bg-sky-600 hover:bg-sky-500 active:bg-sky-700
                             text-white shadow-[0_2px_10px_rgba(14,165,233,0.35)]
                             inline-flex items-center gap-1.5 disabled:opacity-50">
            {busy && <Icon name="progress_activity" size={14} className="animate-spin" />}
            {busy ? 'Queueing…' : 'Queue campaign'}
          </button>
        </div>
        <p className="text-[10.5px] text-arena-600 dark:text-neon-200/55 pt-1">
          Campaign starts within ~5 seconds. Agent-owned players are co-funded
          per the agent's commission terms when applicable.
        </p>
      </motion.div>
    </div>
  );
}

// 2026-05-20 — aliased to canonical INPUT_CLS for dark-mode legibility.
const inputCls = INPUT_CLS;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">{label}</span>
      {children}
    </label>
  );
}
